import { useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '@/auth/useAuth';
import { Card } from '@/components/ui/card';
import { toast } from 'sonner';

interface GoogleCredentialResponse {
  credential: string;
}

interface GoogleAccountsId {
  initialize: (opts: {
    client_id: string;
    callback: (resp: GoogleCredentialResponse) => void;
  }) => void;
  renderButton: (
    el: HTMLElement,
    opts: { theme: string; size: string; locale?: string }
  ) => void;
}

declare global {
  interface Window {
    google?: {
      accounts: {
        id: GoogleAccountsId;
      };
    };
  }
}

const N8N_AUTH_URL = import.meta.env.VITE_N8N_AUTH_URL as string;
const GOOGLE_CLIENT_ID = import.meta.env.VITE_GOOGLE_CLIENT_ID as string;

interface JwtPayload {
  email?: string;
  sub?: string;
  [k: string]: unknown;
}

function decodeJwtPayload(token: string): JwtPayload {
  const parts = token.split('.');
  if (parts.length < 2) throw new Error('JWT invalido: nao tem payload.');
  const payloadSegment = parts[1];
  if (!payloadSegment) throw new Error('JWT invalido: payload vazio.');
  const padded = payloadSegment.padEnd(payloadSegment.length + ((4 - (payloadSegment.length % 4)) % 4), '=');
  const b64 = padded.replace(/-/g, '+').replace(/_/g, '/');
  const json = atob(b64);
  return JSON.parse(json) as JwtPayload;
}

export default function LoginPage() {
  const { login } = useAuth();
  const nav = useNavigate();
  const location = useLocation();
  const btnRef = useRef<HTMLDivElement>(null);
  const from = (location.state as { from?: string } | null)?.from ?? '/empresas';

  useEffect(() => {
    const script = document.createElement('script');
    script.src = 'https://accounts.google.com/gsi/client';
    script.async = true;
    script.onload = () => {
      const accounts = window.google?.accounts.id;
      if (!accounts) {
        toast.error('Google Identity Services nao carregou.');
        return;
      }
      accounts.initialize({
        client_id: GOOGLE_CLIENT_ID,
        callback: async (resp) => {
          try {
            const r = await fetch(N8N_AUTH_URL, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({ id_token: resp.credential }),
            });
            if (!r.ok) {
              toast.error('Login nao autorizado.');
              return;
            }
            // crm-auth v2 devolve JSON { token, email }.
            const data = (await r.json()) as { token?: string; email?: string };
            if (!data.token) {
              toast.error('Resposta de auth sem token.');
              return;
            }
            const payload = decodeJwtPayload(data.token);
            const email =
              data.email ?? (typeof payload.email === 'string' ? payload.email : 'desconhecido');
            login(data.token, email);
            nav(from, { replace: true });
          } catch (e) {
            console.error(e);
            toast.error('Erro de rede no login.');
          }
        },
      });
      if (btnRef.current) {
        accounts.renderButton(btnRef.current, {
          theme: 'outline',
          size: 'large',
          locale: 'pt-BR',
        });
      }
    };
    document.body.appendChild(script);
    return () => {
      if (script.parentNode) script.parentNode.removeChild(script);
    };
  }, [login, nav, from]);

  return (
    <div className="flex h-full items-center justify-center bg-background">
      <Card className="p-8 w-96 space-y-4">
        <h1 className="text-2xl font-semibold">CRM Prospect</h1>
        <p className="text-sm text-muted-foreground">Login restrito.</p>
        <div ref={btnRef} />
      </Card>
    </div>
  );
}
